import { FetchQueryOptions, QueryClient } from "@tanstack/react-query";
import type {
  SessionMethodParams,
  SessionMethodReturn,
  SessionStandardQueryMethodName,
  SessionQueryKey,
  SupportedSession,
} from "../../types";
import { buildSessionQueryKey } from "../../keys";
import { executeTellescopeSessionMethod } from "../execute-tellescope-session-method";
import { TellescopeDefinedQueryOptionKeys } from "../../hooks/types";

type SessionFetchQueryOptions<
  Method extends SessionStandardQueryMethodName,
  TError = Error,
  TData = SessionMethodReturn<Method>,
> = Omit<
  FetchQueryOptions<SessionMethodReturn<Method>, TError, TData, SessionQueryKey<Method>>,
  TellescopeDefinedQueryOptionKeys
>;

export interface FetchSessionQueryArgs<
  Method extends SessionStandardQueryMethodName,
  TError = Error,
  TData = SessionMethodReturn<Method>,
> extends SessionFetchQueryOptions<Method, TError, TData> {
  method: Method;
  request: SessionMethodParams<Method>;
  queryClient: QueryClient;
  session: SupportedSession;
  requestKeysToOmitFromQueryKey?: SessionMethodParams<Method> extends Record<string, unknown>
    ? (keyof SessionMethodParams<Method>)[]
    : never;
}

export const fetchSessionQuery = <
  Method extends SessionStandardQueryMethodName,
  TError = Error,
  TData = SessionMethodReturn<Method>,
>({
  method,
  request,
  queryClient,
  session,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: FetchSessionQueryArgs<Method, TError, TData>) => {
  const queryKey = buildSessionQueryKey({ method, request, requestKeysToOmitFromQueryKey });
  return queryClient.fetchQuery<SessionMethodReturn<Method>, TError, TData, SessionQueryKey<Method>>({
    queryKey,
    queryFn: () =>
      executeTellescopeSessionMethod({
        session,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      }),
    ...tanstackOptions,
  });
};
